// Preflight — pre-session check-in. Replaces the tool picker that used
// to live on the /ai launcher. Every "tool" (vision, drag timeline,
// AeroAI) is always on inside a session now, so the picker was solving
// a problem that doesn't exist; this screen instead helps the rider
// confirm their camera + sensors are wired before they commit.
//
// Layout:
//   ┌─────────────────────────────┬───────────────────────────┐
//   │                             │  Camera  ● Connected      │
//   │   live mirrored camera      │  Sensors                  │
//   │   (large, glowing border)   │  Power   [Connect]        │
//   │                             │  HR      ● Connected      │
//   │                             │  Cadence ● Connected      │
//   │                             │                           │
//   │                             │  [   Start session →   ]  │
//   └─────────────────────────────┴───────────────────────────┘
//
// The camera + Web Bluetooth state are torn down on unmount; the actual
// session view (AI) re-acquires them when it mounts. Brief stream
// flicker is acceptable.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LoopIcon from '@mui/icons-material/Loop';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import AdjustIcon from '@mui/icons-material/Adjust';
import GestureActionBar, { GESTURE_BAR_HEIGHT } from './components/GestureActionBar';
import { useTranslation } from 'react-i18next';
import BluetoothPanel from './components/BluetoothPanel';
import { useBluetooth } from './hooks/useBluetooth';
import { useLiabilityGate } from './components/LiabilityGate';
import { colors } from './theme';
import { PoseDetector, type Landmark, type WorldLandmark } from './vision/poseDetector';
import { HandDetector, handLengthPx, pxPerCmFromHand, type DetectedHand } from './vision/handDetector';
import { labelForPosition } from './vision/positionClassifier';
import { silhouetteToPngDataUrl, frontalAreaFromMask } from './vision/silhouettePng';
import { calibrate, resetCalibration } from './vision/calibration';

interface Props {
  onStart: () => void;
  // Wave 20.44 — the rider picked a position on /session/focus. 'new'
  // when recording a not-yet-known position; a class string when
  // refining; null when they came in the old way (class-agnostic).
  focusClass?: string | null;
}

function focusLabel(focus: string): string {
  if (focus === 'new') return 'New position';
  const known = labelForPosition(focus as any);
  return known === '—' ? focus.replace(/_/g, ' ') : known;
}

type CameraState = 'idle' | 'requesting' | 'ready' | 'denied' | 'unavailable';

// Wave 20.37 — narrator hook. Speaks + logs a coaching prompt.
// Wave 20.44 — narrator UX pass. Rider report: "move a little to
// the left / perfect spot / move a little to the left" oscillating
// while they were just getting on the bike was maddening. The pure
// text-change dedupe wasn't enough because positioning direction
// alternates as they shift. New rule set:
//   1. Never speak more than once every 6 s (COOLDOWN_MS), even if
//      text differs. Logs still update visually so the rider can
//      see the state; only the voice + speech UI is throttled.
//   2. Never repeat the exact same text back-to-back regardless of
//      time (original rule).
//   3. Skip positioning nudges entirely once we've fired 3 in the
//      last 15 s — the rider is actively adjusting and can see
//      the on-screen zone dots without a voice.
const COOLDOWN_MS = 6000;
const POSITIONING_BURST_LIMIT = 3;
const POSITIONING_BURST_WINDOW_MS = 15000;
function useCallbackNarrator(
  voiceEnabled: boolean,
  setLog: React.Dispatch<React.SetStateAction<Array<{ ts: number; text: string; tone: 'info' | 'success' | 'warn' }>>>,
  lastSpokenRef: React.MutableRefObject<{ text: string; ts: number }>,
  positioningTimesRef: React.MutableRefObject<number[]>,
) {
  return useCallback(
    (text: string, tone: 'info' | 'success' | 'warn' = 'info') => {
      if (lastSpokenRef.current.text === text) return;
      const now = Date.now();
      // Positioning nudges are the noisy ones. Track when they
      // fire so we can back off if the rider is shifting a lot.
      const isPositioning =
        text.startsWith('Move ') ||
        text.startsWith('Angle ') ||
        text.startsWith('Adjust ');
      if (isPositioning) {
        const times = positioningTimesRef.current.filter(
          (t) => now - t < POSITIONING_BURST_WINDOW_MS,
        );
        times.push(now);
        positioningTimesRef.current = times;
        if (times.length > POSITIONING_BURST_LIMIT) {
          // Too much wobble — go quiet. On-screen zone dots do the
          // guiding until the rider steadies.
          return;
        }
      }
      // Cooldown: don't stack narrations back-to-back.
      if (now - lastSpokenRef.current.ts < COOLDOWN_MS) return;
      lastSpokenRef.current = { text, ts: now };
      setLog((prev) => {
        const next = [...prev, { ts: now, text, tone }];
        return next.length > 5 ? next.slice(next.length - 5) : next;
      });
      if (voiceEnabled && typeof speechSynthesis !== 'undefined') {
        try {
          speechSynthesis.cancel();
          const u = new SpeechSynthesisUtterance(text);
          u.rate = 1.05;
          u.pitch = 1.0;
          speechSynthesis.speak(u);
        } catch { /* browser refuses or is muted — ignore */ }
      }
    },
    [voiceEnabled, setLog, lastSpokenRef, positioningTimesRef],
  );
}

function StatusPill({
  icon,
  label,
  state,
}: {
  icon: React.ReactNode;
  label: string;
  state: 'on' | 'off' | 'pending';
}) {
  const color =
    state === 'on' ? colors.brand : state === 'pending' ? colors.warning : colors.textFaint;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        p: 1.25,
        borderRadius: 2,
        backgroundColor: colors.surface,
        border: `1px solid ${state === 'on' ? colors.borderFocus : colors.border}`,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor:
            state === 'on' ? 'rgba(106, 255, 69, 0.12)' : colors.surfaceElevated,
          color,
        }}
      >
        {icon}
      </Box>
      <Stack sx={{ flex: 1 }}>
        <Typography
          sx={{
            fontSize: '0.625rem',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: color,
              boxShadow: state === 'on' ? `0 0 8px ${color}` : 'none',
            }}
          />
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: colors.text }}>
            {state === 'on' ? 'Connected' : state === 'pending' ? 'Requesting…' : 'Not connected'}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
}

// Wave 20.37 — zones widened after user feedback that a "perfectly
// framed" rider still got flagged as out-of-zone. These bounds are
// intentionally generous — the point is "is a torso roughly present
// in the top-half + bottom-half of the frame?", not "hit a bullseye".
const SHOULDER_ZONE: [number, number, number, number] = [0.2, 0.8, 0.08, 0.6];
const HIP_ZONE: [number, number, number, number]      = [0.2, 0.8, 0.4,  0.95];
// How long the rider has to hold still (both midpoints in zone AND
// stable) before we call calibration LOCKED.
const HOLD_MS = 3000;
// Position stability tolerance — EMA of shoulder/hip midpoints must
// stay within this delta (normalized coords) for HOLD_MS.
const STABILITY_TOL = 0.03;
// After this many ms of failing to lock, allow the rider to override
// and start anyway ("having trouble?" escape hatch). Not shown before
// then so nobody bypasses it out of impatience alone.
const ESCAPE_MS = 60_000;

// Wave 20.37 — two-pose calibration.
//   detecting       — waiting for pose to appear
//   positioning     — pose visible but not in the upright zones
//   holding         — in-zone + steady, upright pose lock counting down
//   locked_upright  — upright pose captured; prompt for riding pose
//   holding_riding  — rider is stabilising in their riding pose
//   locked          — both poses captured; auto-start countdown fires
// The zone check applies only for the upright pose (a rider in aero
// will not have shoulders in the upper third). Riding-pose lock is a
// pure stability lock — hold whatever position you ride in for 2s.
// Wave 20.49t — `awaiting_start` is the new initial state and the
// post-lock waiting state. In both, the video is up and pose /
// hand detection is running, but the calibration state machine
// doesn't advance until the rider dwells their hand inside the
// on-video virtual circle for 3 seconds (with a spoken 3-2-1
// countdown). This replaces the previous auto-start behavior at
// mount (immediately began looking for a lockable pose) and the
// auto-start countdown after `locked` (Wave 20.37, which fired
// onStart automatically after 3 s and knocked riders out of
// position when they reached for the mouse).
type CalibrationState =
  | 'awaiting_start'
  | 'detecting'
  | 'positioning'
  | 'holding'
  | 'locked_upright'
  | 'holding_riding'
  | 'locked';

const RIDING_HOLD_MS = 2000;

// ================================================================
// Wave 20.36 — Calibration overlays + sidebar card
// ================================================================

function stateColor(s: CalibrationState): string {
  if (s === 'locked' || s === 'locked_upright') return colors.brand;
  if (s === 'holding' || s === 'holding_riding') return colors.brand;
  if (s === 'positioning') return colors.warning;
  return colors.textFaint;
}

function stateLabel(s: CalibrationState): string {
  switch (s) {
    case 'locked':          return 'LOCKED';
    case 'locked_upright':  return 'UPRIGHT LOCKED — GET INTO RIDING POSITION';
    case 'holding_riding':  return 'HOLDING RIDING POSE';
    case 'holding':         return 'HOLDING UPRIGHT';
    case 'positioning':     return 'POSITIONING';
    case 'awaiting_start':  return 'REACH INTO THE CIRCLE TO START';
    default:                return 'DETECTING';
  }
}

// Wave 20.49bm — the ad-hoc virtual Start button was replaced by
// the shared GestureActionTray. VBTN_* constants and
// VirtualStartButton removed.

function CalibrationOverlay({
  calState,
  holdProgress,
  posePoints,
  landmarksRef,
  videoRef,
  calibrationHealth,
  topOffset = { xs: 0, md: 0 },
}: {
  calState: CalibrationState;
  holdProgress: number;
  // Wave 20.49cu — px to push the top-anchored status card + health
  // chip down so they sit below the GestureActionBar strip.
  topOffset?: { xs: number; md: number };
  posePoints: {
    shoulderMid: { x: number; y: number } | null;
    hipMid: { x: number; y: number } | null;
  };
  // Wave 20.49w — the full MediaPipe pose landmark array from
  // the parent tick loop. PreflightSkeletonOverlay reads this
  // ref directly at 60 fps via requestAnimationFrame so the
  // skeleton renders smooth even though the tick fires at 5 Hz.
  landmarksRef: React.MutableRefObject<Landmark[] | null>;
  // Wave 20.49x — needed by the skeleton overlay to apply
  // object-fit: cover math to landmark positions.
  videoRef: React.RefObject<HTMLVideoElement>;
  // Wave 20.49u — live calibrate() ensemble agreement 0..1.
  // Surfaced as the SCALE X% chip in the corner.
  calibrationHealth: number;
}) {
  // Determine whether each zone contains its target midpoint — for
  // per-zone tint.
  const inShoulder =
    posePoints.shoulderMid &&
    posePoints.shoulderMid.x >= SHOULDER_ZONE[0] &&
    posePoints.shoulderMid.x <= SHOULDER_ZONE[1] &&
    posePoints.shoulderMid.y >= SHOULDER_ZONE[2] &&
    posePoints.shoulderMid.y <= SHOULDER_ZONE[3];
  const inHip =
    posePoints.hipMid &&
    posePoints.hipMid.x >= HIP_ZONE[0] &&
    posePoints.hipMid.x <= HIP_ZONE[1] &&
    posePoints.hipMid.y >= HIP_ZONE[2] &&
    posePoints.hipMid.y <= HIP_ZONE[3];
  const zoneColor = (inZone: boolean | null) =>
    calState === 'locked'
      ? colors.brand
      : inZone
      ? colors.brand
      : colors.warning;

  const zoneStyle = (
    z: [number, number, number, number],
    inZone: boolean | null,
  ): React.CSSProperties => ({
    position: 'absolute',
    left: `${z[0] * 100}%`,
    top: `${z[2] * 100}%`,
    width: `${(z[1] - z[0]) * 100}%`,
    height: `${(z[3] - z[2]) * 100}%`,
    border: `2px dashed ${zoneColor(inZone)}`,
    borderRadius: 8,
    opacity: 0.75,
    boxShadow: inZone ? `0 0 20px ${zoneColor(inZone)}55 inset` : 'none',
    pointerEvents: 'none',
    transition: 'border-color 200ms, box-shadow 200ms',
  });

  return (
    <>
      {/* Zones */}
      <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <Box sx={zoneStyle(SHOULDER_ZONE, inShoulder)} />
        <Box sx={zoneStyle(HIP_ZONE, inHip)} />
        {/* Vertical center guide */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '10%',
            bottom: '10%',
            width: '1px',
            backgroundColor: 'rgba(255,255,255,0.15)',
          }}
        />
        {/* Wave 20.49p — Ghost target dots removed. The old
            "align to yesterday's dots" gate broke the day a rider
            switched cameras. Now the live shoulder/hip dots (drawn
            below) already show where the rider is and the lock
            captures a fresh reference every session. */}
        {/* Wave 20.49w — Full MediaPipe skeleton overlay drawn on
            a canvas. Same feature as the live session's
            VisionOverlay skeleton layer: every landmark (face,
            arms, torso, hands, legs, feet) with depth-shaded
            bones + joint dots. The sparse 6-segment SVG version
            (Wave 20.49u) was replaced because it was hiding how
            noisy the raw pose was — the full skeleton makes the
            calibration state legible in one look. */}
        <PreflightSkeletonOverlay landmarksRef={landmarksRef} videoRef={videoRef} />
        {/* Wave 20.49u — Calibration health chip. Feedback for the
            AND gate: if segments don't agree within 8%, the state
            machine won't lock, so surface the number. */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 12 + topOffset.xs, md: 12 + topOffset.md },
            right: 12,
            px: 1,
            py: 0.5,
            borderRadius: 999,
            fontSize: '0.7rem',
            fontWeight: 700,
            fontFamily: '"JetBrains Mono", monospace',
            letterSpacing: '0.05em',
            color:
              calibrationHealth >= 0.6
                ? colors.brand
                : calibrationHealth >= 0.4
                ? colors.warning
                : colors.danger,
            backgroundColor: 'rgba(0,0,0,0.55)',
            border: `1px solid ${
              calibrationHealth >= 0.4 ? colors.brand : colors.warning
            }`,
          }}
        >
          {`SCALE ${Math.round(calibrationHealth * 100)}%`}
        </Box>
        {/* Shoulder midpoint dot — big enough to see while adjusting */}
        {posePoints.shoulderMid && (
          <Box
            sx={{
              position: 'absolute',
              left: `${posePoints.shoulderMid.x * 100}%`,
              top: `${posePoints.shoulderMid.y * 100}%`,
              transform: 'translate(-50%, -50%)',
              width: 14,
              height: 14,
              borderRadius: '50%',
              backgroundColor: zoneColor(inShoulder),
              border: '2px solid #0e0e12',
              boxShadow: `0 0 12px ${zoneColor(inShoulder)}`,
              transition: 'background-color 200ms',
            }}
          />
        )}
        {posePoints.hipMid && (
          <Box
            sx={{
              position: 'absolute',
              left: `${posePoints.hipMid.x * 100}%`,
              top: `${posePoints.hipMid.y * 100}%`,
              transform: 'translate(-50%, -50%)',
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: zoneColor(inHip),
              border: '2px solid #0e0e12',
              opacity: 0.85,
            }}
          />
        )}
      </Box>

      {/* Status card top-center (below the GestureActionBar strip) */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 16 + topOffset.xs, md: 16 + topOffset.md },
          left: '50%',
          transform: 'translateX(-50%)',
          px: 2,
          py: 1,
          borderRadius: 999,
          backgroundColor: 'rgba(15,15,20,0.9)',
          border: `1.5px solid ${stateColor(calState)}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minWidth: 200,
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        {calState === 'locked' ? (
          <CheckCircleIcon sx={{ color: colors.brand, fontSize: 18 }} />
        ) : (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: stateColor(calState),
              boxShadow: `0 0 8px ${stateColor(calState)}`,
              animation: calState === 'detecting' ? 'preflightPulse 1.2s ease-in-out infinite' : 'none',
              '@keyframes preflightPulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.35 },
              },
            }}
          />
        )}
        <Typography
          sx={{
            color: colors.text,
            fontSize: '0.85rem',
            fontWeight: 800,
            letterSpacing: '0.1em',
          }}
        >
          {stateLabel(calState)}
          {calState === 'holding' && ` ${Math.ceil((1 - holdProgress) * 3)}…`}
          {calState === 'holding_riding' && ` ${Math.ceil((1 - holdProgress) * 2)}…`}
        </Typography>
      </Box>

      {/* Progress bar during either HOLDING phase (upright or riding). */}
      {(calState === 'holding' || calState === 'holding_riding') && (
        <Box
          sx={{
            position: 'absolute',
            top: 48,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 200,
            height: 3,
            borderRadius: 2,
            backgroundColor: 'rgba(255,255,255,0.1)',
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              width: `${holdProgress * 100}%`,
              height: '100%',
              backgroundColor: colors.brand,
              transition: 'width 200ms linear',
            }}
          />
        </Box>
      )}
    </>
  );
}

// Wave 20.49w — Full MediaPipe skeleton overlay for Preflight.
// Mirrors what the live session's VisionOverlay draws: every
// pose landmark (nose, eyes, ears, mouth, shoulders, elbows,
// wrists, hand tips, hips, knees, ankles, heels, foot tips) +
// the bones connecting them, depth-shaded, with joint dots.
// Reads the shared `landmarksRef` at 60 fps via RAF so the
// skeleton stays smooth between MediaPipe detect() calls
// (which fire at ~5 Hz in Preflight).
const POSE_EDGES_FULL: [number, number][] = [
  // Face
  [0, 1], [1, 2], [2, 3], [3, 7],
  [0, 4], [4, 5], [5, 6], [6, 8],
  [9, 10],
  // Shoulders + torso
  [11, 12], [11, 23], [12, 24], [23, 24],
  // Arms
  [11, 13], [13, 15], [15, 17], [15, 19], [15, 21], [17, 19],
  [12, 14], [14, 16], [16, 18], [16, 20], [16, 22], [18, 20],
  // Legs
  [23, 25], [25, 27], [27, 29], [27, 31], [29, 31],
  [24, 26], [26, 28], [28, 30], [28, 32], [30, 32],
];

function PreflightSkeletonOverlay({
  landmarksRef,
  videoRef,
}: {
  landmarksRef: React.MutableRefObject<Landmark[] | null>;
  // Wave 20.49x — needed for object-fit: cover math. The
  // Preflight <video> is a 3:4 container displaying a 4:3 or
  // 16:9 webcam feed with objectFit: 'cover', so the video is
  // scaled to FILL the container and the horizontal edges are
  // cropped off. Pose landmarks are in normalized video coords
  // (0..1 of the ORIGINAL video pixels), not container coords —
  // so drawing them at `lm.x * canvasW` puts them in the wrong
  // place. We read videoWidth / videoHeight to compute the same
  // fit + offset the browser is using, then draw against that.
  videoRef: React.RefObject<HTMLVideoElement>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) return;
      canvas.width = w * window.devicePixelRatio;
      canvas.height = h * window.devicePixelRatio;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let raf = 0;
    const draw = () => {
      raf = requestAnimationFrame(draw);
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      const landmarks = landmarksRef.current;
      if (!landmarks || landmarks.length < 33) return;
      // Wave 20.49x — mirror the object-fit: cover transform the
      // browser is applying to the underlying <video>. Without
      // this, landmarks whose normalized x is near 0 or 1 land
      // way off screen and the whole skeleton compresses toward
      // the container centerline.
      const video = videoRef.current;
      const vw = video?.videoWidth || 0;
      const vh = video?.videoHeight || 0;
      if (!vw || !vh) return;
      const containerAspect = w / h;
      const videoAspect = vw / vh;
      let fittedW: number;
      let fittedH: number;
      let offsetX = 0;
      let offsetY = 0;
      if (videoAspect > containerAspect) {
        // Video is proportionally wider than container: fit
        // height, crop sides.
        fittedH = h;
        fittedW = h * videoAspect;
        offsetX = (w - fittedW) / 2;
      } else {
        // Video is proportionally taller than container: fit
        // width, crop top/bottom.
        fittedW = w;
        fittedH = w / videoAspect;
        offsetY = (h - fittedH) / 2;
      }
      // Video is CSS-mirrored (scaleX(-1)) in Preflight; mirror
      // the landmark x so bones land on the rider's actual body.
      const px = (lm: Landmark) => (1 - lm.x) * fittedW + offsetX;
      const py = (lm: Landmark) => lm.y * fittedH + offsetY;

      // Bones — depth-shaded green (matches the live session).
      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineWidth = Math.max(2.5, w / 500);
      for (const [a, b] of POSE_EDGES_FULL) {
        const la = landmarks[a];
        const lb = landmarks[b];
        if (!la || !lb) continue;
        if ((la.visibility ?? 0) < 0.4 || (lb.visibility ?? 0) < 0.4) continue;
        const avgZ = (la.z + lb.z) / 2;
        const depth = Math.max(0, Math.min(1, 0.5 - avgZ));
        const alpha = 0.55 + depth * 0.45;
        ctx.strokeStyle = `rgba(106, 255, 69, ${alpha})`;
        ctx.shadowColor = colors.brand;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.moveTo(px(la), py(la));
        ctx.lineTo(px(lb), py(lb));
        ctx.stroke();
      }
      // Joints — white dots, depth-shaded.
      ctx.shadowBlur = 0;
      for (const lm of landmarks) {
        if ((lm.visibility ?? 0) < 0.4) continue;
        const depth = Math.max(0, Math.min(1, 0.5 - lm.z));
        const r = Math.max(3, w / 340) * (0.7 + depth * 0.6);
        ctx.fillStyle = `rgba(255, 255, 255, ${0.7 + depth * 0.3})`;
        ctx.beginPath();
        ctx.arc(px(lm), py(lm), r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [landmarksRef]);
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
}

// Wave 20.49bm — VirtualStartButton replaced by the shared
// GestureActionTray, since replaced by GestureActionBar. See
// web/src/components/GestureActionBar.tsx.

function CalibrationCard({
  calState,
  holdProgress,
  voiceEnabled,
  onToggleVoice,
  chatLog,
}: {
  calState: CalibrationState;
  holdProgress: number;
  voiceEnabled: boolean;
  onToggleVoice: () => void;
  chatLog: Array<{ ts: number; text: string; tone: 'info' | 'success' | 'warn' }>;
}) {
  const color = stateColor(calState);
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        backgroundColor: colors.surface,
        border: `1px solid ${calState === 'locked' ? colors.borderFocus : colors.border}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor:
              calState === 'locked' ? 'rgba(106, 255, 69, 0.12)' : colors.surfaceElevated,
          }}
        >
          {calState === 'locked' ? (
            <CheckCircleIcon sx={{ color: colors.brand, fontSize: 20 }} />
          ) : (
            <AccessibilityNewIcon sx={{ color: color, fontSize: 18 }} />
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: colors.textMuted,
            }}
          >
            Calibration
          </Typography>
          <Typography sx={{ color, fontSize: '0.85rem', fontWeight: 800 }}>
            {stateLabel(calState)}
            {calState === 'holding' && ` ${Math.ceil((1 - holdProgress) * 3)}s…`}
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={onToggleVoice}
          aria-label={voiceEnabled ? 'Mute voice' : 'Unmute voice'}
          sx={{
            minWidth: 0,
            p: 0.5,
            color: voiceEnabled ? colors.brand : colors.textFaint,
          }}
        >
          {voiceEnabled ? (
            <VolumeUpIcon sx={{ fontSize: 18 }} />
          ) : (
            <VolumeOffIcon sx={{ fontSize: 18 }} />
          )}
        </Button>
      </Box>
      {/* Chat log — most recent last, dimmer as they age */}
      {chatLog.length > 0 && (
        <Stack spacing={0.5} sx={{ pt: 0.5, borderTop: `1px solid ${colors.border}` }}>
          {chatLog.map((entry, i) => {
            const age = chatLog.length - 1 - i;
            const opacity = 1 - age * 0.15;
            const tint =
              entry.tone === 'success'
                ? colors.brand
                : entry.tone === 'warn'
                ? colors.warning
                : colors.textMuted;
            return (
              <Typography
                key={entry.ts + '-' + i}
                sx={{
                  fontSize: '0.72rem',
                  color: tint,
                  opacity: Math.max(0.4, opacity),
                  lineHeight: 1.3,
                  mt: 0.5,
                }}
              >
                {entry.text}
              </Typography>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

export default function Preflight({ onStart, focusClass }: Props) {
  const { t } = useTranslation('dashboard');
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraState, setCameraState] = useState<CameraState>('idle');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const ble = useBluetooth();
  const liability = useLiabilityGate();

  // Wave 20.36 — calibration lock state machine. Runs in the preflight
  // pose loop below; state transitions drive the on-video overlays,
  // chat log, voice prompts, and the Start button gate.
  const [calState, setCalState] = useState<CalibrationState>('awaiting_start');
  const [holdProgress, setHoldProgress] = useState(0); // 0..1
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [escapeAllowed, setEscapeAllowed] = useState(false);
  const [chatLog, setChatLog] = useState<
    Array<{ ts: number; text: string; tone: 'info' | 'success' | 'warn' }>
  >([]);
  // Where the pose currently is on-screen — drives the centering-dot
  // and zone-highlight overlays. Nullable so overlays hide before we
  // have any detection.
  const [posePoints, setPosePoints] = useState<{
    shoulderMid: { x: number; y: number } | null;
    hipMid: { x: number; y: number } | null;
  }>({ shoulderMid: null, hipMid: null });
  // Wave 20.49w — collapsed. The sparse per-endpoint SVG segment
  // state got replaced by the full MediaPipe skeleton canvas
  // overlay, which reads `latestLandmarksRef` directly. Only
  // the live calibrate() ensemble health remains as React state
  // (drives the SCALE X% chip in the corner and the lock gate).
  const [calibrationHealth, setCalibrationHealth] = useState(0);

  const calStateRef = useRef<CalibrationState>('awaiting_start');
  // Wave 20.49bm — Gesture state now lives inside GestureActionTray.
  // Only the one-shot prompt bookkeeping stays here.
  const gesturePromptShownRef = useRef<{
    awaiting_start: boolean;
    locked: boolean;
  }>({ awaiting_start: false, locked: false });
  const holdStartRef = useRef<number | null>(null);
  const stabilityHistoryRef = useRef<
    Array<{ ts: number; sx: number; sy: number; hx: number; hy: number }>
  >([]);
  const lastSpokenRef = useRef<{ text: string; ts: number }>({ text: '', ts: 0 });
  // Wave 20.44 — track positioning-prompt bursts for the narrator
  // back-off (see useCallbackNarrator).
  const positioningTimesRef = useRef<number[]>([]);
  // Wave 20.37 — snapshots of the two calibration poses. Written by
  // the tick loop when each lock condition is met, persisted to
  // localStorage under 'aerochat.calibrationPoses' for the live
  // session (AIClientVision) to pick up as its per-position baseline.
  const uprightPoseRef = useRef<{ sx: number; sy: number; hx: number; hy: number } | null>(null);
  const ridingPoseRef  = useRef<{ sx: number; sy: number; hx: number; hy: number } | null>(null);

  // Wave 20.49k — Repeatable baseline framing target. Loaded once
  // on mount from GET /users/me/calibration-target. When present,
  // upright-lock requires the rider's shoulder + hip midpoints to
  // sit within TARGET_TOL of the saved dots AND the pixel distance
  // between them to match within TARGET_SCALE_TOL. First-time
  // riders (target === null) auto-capture on their very first
  // successful upright lock so every future session compares to
  // the same framing.
  type CalibrationTarget = {
    shoulderMid: { x: number; y: number };
    hipMid: { x: number; y: number };
    shoulderHipPx: number;
    capturedAt: string;
  };
  const targetRef = useRef<CalibrationTarget | null>(null);
  // Wave 20.49q — Latest MediaPipe segmentation mask; snapshotted
  // at upright-lock time by silhouetteToPngDataUrl. Written every
  // tick; only read at the moment of lock.
  const latestMaskRef = useRef<ImageData | null>(null);
  // Wave 20.49s — stash the raw landmarks + worldLandmarks + frame
  // dimensions from the most recent tick so the lock handler can
  // run auto-calibration (calibrate()) and back-solve px/m at the
  // exact moment we snapshot A_cal. Same lifecycle as latestMaskRef.
  const latestLandmarksRef = useRef<Landmark[] | null>(null);
  const latestWorldLandmarksRef = useRef<WorldLandmark[] | null>(null);
  // Wave 20.49q — Voice guidance state:
  //   detectStartMs: when we first saw landmarks. Copy is delayed
  //     until 2 s of continuous detection so a rider who lands
  //     out-of-zone doesn't get hammered on frame 1.
  //   guidedPromptShownRef: fires the "top box / bottom box"
  //     guidance sentence exactly once per session.
  //   outOfZoneStartMs: when the rider first went out-of-zone
  //     without recovering. If they're still out at 6 s, we drop
  //     into advisory mode and stability alone locks (Wave 20.49p
  //     escape hatch, preserved).
  const detectStartMsRef = useRef<number | null>(null);
  const guidedPromptShownRef = useRef(false);
  const outOfZoneStartMsRef = useRef<number | null>(null);
  // Wave 20.37 — Latest hand detection snapshot, refreshed each tick
  // by the HandDetector. Read at riding-pose lock time so we don't
  // re-detect on demand.
  const latestHandsRef = useRef<DetectedHand[]>([]);

  // Push a message into the chat log AND (optionally) speak it. Dedupes
  // consecutive identical messages within 4 s so a state that oscillates
  // doesn't spam the log.
  const say = useCallbackNarrator(voiceEnabled, setChatLog, lastSpokenRef, positioningTimesRef);

  // Wave 20.49k — fetch the rider's saved framing target once at
  // mount. Null response = first-time or reset; the auto-capture
  // branch below will PUT after the first successful upright lock.
  useEffect(() => {
    const token = (() => {
      try { return window.localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token'); }
      catch { return null; }
    })();
    if (!token) return;
    const api = process.env.REACT_APP_API || 'https://api.aero.chat';
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${api}/users/me/calibration-target`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const body: CalibrationTarget | null = await res.json();
        if (body && body.shoulderMid && body.hipMid) {
          targetRef.current = body;
        }
      } catch { /* silent — falls back to zone-only lock */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wave 20.44 — Sensor-pairing gate. Rider report: the flow used
  // to auto-start calibration + countdown before they'd had a
  // chance to pair a power meter or HR monitor. Adds an explicit
  // "Pair your sensors" step: modal opens on camera-ready, blocks
  // calibration + auto-start until the rider picks a path
  // (Connect one, or Continue without). Sensor connections happen
  // in the modal but don't dismiss it — the rider explicitly
  // taps Continue when ready.
  const [sensorsConfirmed, setSensorsConfirmed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const acquire = async () => {
      setCameraState('requesting');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setCameraState('ready');
      } catch (e: any) {
        if (cancelled) return;
        const msg = e?.message ?? 'Camera unavailable';
        if (e?.name === 'NotAllowedError') {
          setCameraState('denied');
          setCameraError('Camera access was denied. Allow it in your browser settings to continue.');
        } else if (e?.name === 'NotFoundError') {
          setCameraState('unavailable');
          setCameraError('No camera found. Plug one in to continue.');
        } else {
          setCameraState('unavailable');
          setCameraError(msg);
        }
      }
    };
    acquire();
    return () => {
      cancelled = true;
      const stream = streamRef.current;
      if (stream) {
        stream.getTracks().forEach((tr) => tr.stop());
      }
      streamRef.current = null;
    };
  }, []);

  // Wave 20.36 — MediaPipe pose loop that drives the CALIBRATION LOCK
  // state machine. Runs at ~5 Hz on the preflight video; teardown
  // cancels the loop + closes the detector.
  //
  // State machine:
  //   detecting   → no landmarks yet
  //   positioning → landmarks seen but shoulder/hip midpoints outside
  //                 the target zones (or off-center, or too far)
  //   holding     → in zones + confidence OK; countdown to lock begins
  //   locked      → held stable for HOLD_MS; Start button unlocks
  useEffect(() => {
    if (cameraState !== 'ready') return;
    // Wave 20.44 — hold calibration until the rider has picked a
    // sensor path. Otherwise the session used to auto-start before
    // they'd had time to pair a power meter or HR monitor.
    if (!sensorsConfirmed) return;
    const detector = new PoseDetector();
    // Wave 20.37 — HandDetector runs in parallel with PoseDetector so
    // the riding-pose capture can also snapshot hand landmarks +
    // derive a pixel-to-cm scale (from measured hand length vs
    // population avg 18.5 cm). Init failure is non-fatal — pose
    // calibration still works, we just skip the depth scale.
    const handDetector = new HandDetector();
    let handReady = false;
    handDetector.init().then(() => { handReady = true; }).catch(() => {});
    let cancelled = false;
    let timer: number | null = null;
    let firstFrameSeen = false;
    setCalState('awaiting_start');
    calStateRef.current = 'awaiting_start';
    // Wave 20.49u — clear the calibrate() module-level EMA state
    // so this Preflight mount starts with fresh body-dimension
    // measurements, not whatever was cached from a previous
    // session's rider or lighting. Cheap: ~10 frames to re-warm.
    try { resetCalibration(); } catch { /* ok */ }
    setHoldProgress(0);
    holdStartRef.current = null;
    stabilityHistoryRef.current = [];

    const setStateBoth = (s: CalibrationState) => {
      calStateRef.current = s;
      setCalState(s);
    };

    const tick = () => {
      if (cancelled) return;
      const v = videoRef.current;
      if (!v || v.readyState < 2) {
        timer = window.setTimeout(tick, 200);
        return;
      }
      let res: ReturnType<PoseDetector['detect']> | null = null;
      try {
        res = detector.detect(v, performance.now());
      } catch {
        // Detector still initialising or a transient failure.
      }
      // Sample hands on every tick — cheap and we need them fresh at
      // the moment of riding-pose lock. Snapshot latest into a ref
      // instead of firing detection on demand (avoids race with the
      // WASM detector's internal timestamp check).
      if (handReady) {
        try {
          const hr = handDetector.detect(v, performance.now());
          latestHandsRef.current = hr?.hands ?? [];
        } catch {
          // Ignore; may be transient WASM contention with the pose call.
        }
      }
      const now = Date.now();

      if (!res || !res.landmarks) {
        if (calStateRef.current !== 'detecting') setStateBoth('detecting');
        setHoldProgress(0);
        holdStartRef.current = null;
        stabilityHistoryRef.current = [];
        setPosePoints({ shoulderMid: null, hipMid: null });
        // Wave 20.49q — reset detection anchors so the guidance
        // timing restarts when the rider steps back into frame.
        detectStartMsRef.current = null;
        outOfZoneStartMsRef.current = null;
        if (firstFrameSeen) {
          say('Step in front of the camera so I can see you.', 'warn');
        }
        timer = window.setTimeout(tick, 200);
        return;
      }
      firstFrameSeen = true;
      // Wave 20.49q — stash the latest mask so the moment of
      // upright lock can snapshot it. detect() re-runs every
      // 200 ms so the mask is at most one tick stale.
      latestMaskRef.current = res.segmentationMask ?? latestMaskRef.current;
      latestLandmarksRef.current = res.landmarks ?? latestLandmarksRef.current;
      latestWorldLandmarksRef.current =
        res.worldLandmarks ?? latestWorldLandmarksRef.current;
      if (detectStartMsRef.current == null) detectStartMsRef.current = now;
      const lm = res.landmarks;

      // Video is mirrored horizontally in the CSS (scaleX(-1)). To
      // draw overlays that match what the rider sees on screen, mirror
      // the landmark x too. Y is unchanged.
      const mirrorX = (x: number) => 1 - x;
      const sMidX = mirrorX(((lm[11]?.x ?? 0.5) + (lm[12]?.x ?? 0.5)) / 2);
      const sMidY = ((lm[11]?.y ?? 0.5) + (lm[12]?.y ?? 0.5)) / 2;
      const hMidX = mirrorX(((lm[23]?.x ?? 0.5) + (lm[24]?.x ?? 0.5)) / 2);
      const hMidY = ((lm[23]?.y ?? 0.5) + (lm[24]?.y ?? 0.5)) / 2;
      const shoulderVis =
        ((lm[11]?.visibility ?? 0) + (lm[12]?.visibility ?? 0)) / 2;
      const hipVis =
        ((lm[23]?.visibility ?? 0) + (lm[24]?.visibility ?? 0)) / 2;

      setPosePoints({
        shoulderMid: { x: sMidX, y: sMidY },
        hipMid: { x: hMidX, y: hMidY },
      });

      const imgW = v.videoWidth || 640;
      const imgH = v.videoHeight || 480;
      // Wave 20.49u — Per-frame body-segment auto-calibration.
      // Same function the AI session runs — gives us the scale
      // health (0..1). We gate `goodConfidence` on this in
      // addition to raw landmark visibility, so a session where
      // MediaPipe INFERS an off-frame landmark can no longer
      // fake its way past the lock: calibrate() will refuse the
      // ensemble and calibration_health stays near zero.
      const calResult = (() => {
        try {
          return calibrate(res.landmarks, res.worldLandmarks, imgW, imgH);
        } catch {
          return null;
        }
      })();
      const nextHealth = calResult?.calibration_health ?? 0;
      setCalibrationHealth(nextHealth);

      // Zone checks (using target zones, in mirrored coords).
      const inZone = (
        x: number,
        y: number,
        z: [number, number, number, number],
      ) => x >= z[0] && x <= z[1] && y >= z[2] && y <= z[3];
      const shoulderInZone = inZone(sMidX, sMidY, SHOULDER_ZONE);
      const hipInZone = inZone(hMidX, hMidY, HIP_ZONE);
      // Wave 20.49v — Confidence gate now also requires LEGS in
      // frame. The rider asked to make sure legs (hips → knees)
      // are visible before starting, and it also stabilizes the
      // per-frame CdA: the thigh + shin segments barely
      // foreshorten across upright/hoods/aero, so once they
      // anchor the scale the aero-inverted-drag bug goes away.
      const kneeVis =
        ((lm[25]?.visibility ?? 0) + (lm[26]?.visibility ?? 0)) / 2;
      const goodConfidence =
        shoulderVis > 0.35 &&
        hipVis > 0.35 &&
        kneeVis > 0.35 &&
        (calResult?.is_usable ?? false);

      // Stability window — keep last 1.5 s of positions, check max-min.
      const hist = stabilityHistoryRef.current;
      hist.push({ ts: now, sx: sMidX, sy: sMidY, hx: hMidX, hy: hMidY });
      const cutoff = now - 1500;
      while (hist.length > 0 && hist[0].ts < cutoff) hist.shift();
      const spread = (get: (p: (typeof hist)[number]) => number) => {
        if (hist.length < 3) return Number.POSITIVE_INFINITY;
        let mn = Infinity, mx = -Infinity;
        for (const p of hist) {
          const v = get(p);
          if (v < mn) mn = v;
          if (v > mx) mx = v;
        }
        return mx - mn;
      };
      const stable =
        spread((p) => p.sx) < STABILITY_TOL &&
        spread((p) => p.sy) < STABILITY_TOL &&
        spread((p) => p.hx) < STABILITY_TOL &&
        spread((p) => p.hy) < STABILITY_TOL;

      const cur = calStateRef.current;

      // Wave 20.49t — Virtual Start button gate. When we're in
      // `awaiting_start` (initial mount) or `locked` (calibration
      // complete, waiting to launch the session), the calibration
      // state machine is paused and the rider drives the next
      // step by hovering their hand inside the on-video circle.
      if (cur === 'awaiting_start' || cur === 'locked') {
        // Wave 20.49bm — Hit-test + dwell now owned by
        // GestureActionTray (rendered inside the video box).
        // The tick loop just fires the one-shot voice prompt on
        // state entry so the rider knows what to do.
        const key = cur === 'awaiting_start' ? 'awaiting_start' : 'locked';
        if (!gesturePromptShownRef.current[key]) {
          gesturePromptShownRef.current[key] = true;
          if (cur === 'awaiting_start') {
            say(
              'When you\'re ready, hover the Start Calibration button — or make a fist to fire immediately.',
              'info',
            );
          } else {
            say(
              'Calibration complete. Hover Start Session, or make a fist to go now.',
              'success',
            );
          }
        }
        timer = window.setTimeout(tick, 200);
        return;
      }

      // Wave 20.37 — Two-pose calibration flow.
      //
      // Once the upright pose has locked, we STOP applying zone
      // checks — the rider will now shift into their riding
      // position (aero / hoods / drops) which is unambiguously
      // outside the "sit up straight" shoulder/hip zones. The
      // riding pose lock is a pure stability lock: hold whatever
      // riding position for RIDING_HOLD_MS, we snapshot it, done.
      if (cur === 'locked_upright' || cur === 'holding_riding') {
        if (!goodConfidence) {
          setStateBoth('positioning');
          setHoldProgress(0);
          holdStartRef.current = null;
          say('I lost you — get back in frame and we\'ll re-lock.', 'warn');
        } else if (!stable) {
          if (cur !== 'locked_upright') {
            setStateBoth('locked_upright');
            say('Keep adjusting — I\'ll capture when you\'re steady.', 'info');
          }
          holdStartRef.current = null;
          setHoldProgress(0);
        } else {
          if (cur !== 'holding_riding') {
            setStateBoth('holding_riding');
            say('Perfect riding position — hold it…', 'info');
          }
          if (holdStartRef.current == null) holdStartRef.current = now;
          const held = now - holdStartRef.current;
          const prog = Math.min(1, held / RIDING_HOLD_MS);
          setHoldProgress(prog);
          if (held >= RIDING_HOLD_MS) {
            ridingPoseRef.current = { sx: sMidX, sy: sMidY, hx: hMidX, hy: hMidY };

            // Wave 20.37 — Derive depth scale + bar reach from the
            // hand landmarks currently detected. We prefer taking
            // the mean of both hands (more robust than either one
            // alone) and fall back to whatever we've got.
            const v = videoRef.current;
            const imgW = v?.videoWidth ?? 640;
            const imgH = v?.videoHeight ?? 480;
            const hands = latestHandsRef.current;
            let pxPerCm: number | null = null;
            let handLenPxAvg: number | null = null;
            if (hands.length > 0) {
              const perHand: number[] = [];
              for (const h of hands) {
                const p = pxPerCmFromHand(h, imgW, imgH);
                if (p != null) perHand.push(p);
                const lp = handLengthPx(h, imgW, imgH);
                if (lp != null && lp > 5) {
                  handLenPxAvg =
                    handLenPxAvg == null ? lp : (handLenPxAvg + lp) / 2;
                }
              }
              if (perHand.length > 0) {
                pxPerCm = perHand.reduce((a, b) => a + b, 0) / perHand.length;
              }
            }

            // Bar reach = shoulder → wrist pixel distance. Pose model
            // gives us shoulder (11 / 12) and wrist (15 / 16); take
            // the mean of both sides that are visible.
            const lShoulder = res!.landmarks![11];
            const rShoulder = res!.landmarks![12];
            const lWrist    = res!.landmarks![15];
            const rWrist    = res!.landmarks![16];
            const sides: number[] = [];
            const reachPx = (a: any, b: any): number | null => {
              if (!a || !b || (a.visibility ?? 0) < 0.4 || (b.visibility ?? 0) < 0.4) return null;
              return Math.hypot((a.x - b.x) * imgW, (a.y - b.y) * imgH);
            };
            const lr = reachPx(lShoulder, lWrist);
            const rr = reachPx(rShoulder, rWrist);
            if (lr != null) sides.push(lr);
            if (rr != null) sides.push(rr);
            const barReachPx =
              sides.length > 0 ? sides.reduce((a, b) => a + b, 0) / sides.length : null;
            const barReachCm =
              pxPerCm != null && barReachPx != null ? barReachPx / pxPerCm : null;

            try {
              window.localStorage.setItem(
                'aerochat.calibrationPoses',
                JSON.stringify({
                  upright: uprightPoseRef.current,
                  riding: ridingPoseRef.current,
                  handLenPx: handLenPxAvg,
                  pxPerCm,
                  barReachPx,
                  barReachCm,
                  capturedAt: now,
                }),
              );
            } catch { /* storage disabled — pose is still in-memory */ }
            setStateBoth('locked');
            setHoldProgress(1);
            const scaleNote =
              barReachCm != null
                ? ` Bar reach ~${Math.round(barReachCm)} cm.`
                : '';
            say(`Calibration locked.${scaleNote}`, 'success');
          }
        }
      } else if (!goodConfidence) {
        if (cur !== 'positioning') setStateBoth('positioning');
        setHoldProgress(0);
        holdStartRef.current = null;
        // Reset the out-of-zone timer whenever confidence drops
        // — we don't want a low-vis frame to count toward the
        // 6 s advisory-escape budget.
        outOfZoneStartMsRef.current = null;
        say('Body not clear enough — better light or step forward.', 'warn');
      } else {
        // Wave 20.49q — Guided calibration flow, restored with
        // a soft-zone gate + advisory escape.
        //
        // Rules:
        //   1. On first detection, wait 2 s of continuous detection
        //      before offering guidance so a rider who just walked
        //      into frame isn't shouted at.
        //   2. Once past that grace, if either midpoint is out of
        //      its zone, narrate ONCE: "Put your shoulders in the
        //      top box and your hips in the bottom box." Then coach
        //      with directional nudges when a zone gap persists.
        //   3. When both midpoints are IN their zones AND stable
        //      AND held for HOLD_MS, lock (below).
        //   4. Advisory escape (Wave 20.49p intent preserved): if
        //      the rider is confidently detected but stays out of
        //      zone for > 6 s, drop the zone requirement and let
        //      stability alone lock. Prevents a weird camera angle
        //      from stranding the rider forever.
        const OUT_OF_ZONE_ESCAPE_MS = 6000;
        const GRACE_MS = 2000;
        const detectedForMs =
          detectStartMsRef.current != null ? now - detectStartMsRef.current : 0;
        const inZones = shoulderInZone && hipInZone;
        if (inZones) {
          outOfZoneStartMsRef.current = null;
        } else if (outOfZoneStartMsRef.current == null) {
          outOfZoneStartMsRef.current = now;
        }
        const advisoryEscape =
          outOfZoneStartMsRef.current != null &&
          now - outOfZoneStartMsRef.current > OUT_OF_ZONE_ESCAPE_MS;
        const zoneOk = inZones || advisoryEscape;

        if (!zoneOk && detectedForMs > GRACE_MS) {
          if (cur !== 'positioning') setStateBoth('positioning');
          setHoldProgress(0);
          holdStartRef.current = null;
          if (!guidedPromptShownRef.current) {
            guidedPromptShownRef.current = true;
            say(
              'Put your shoulders in the top box and your hips in the bottom box.',
              'info',
            );
          } else if (!shoulderInZone) {
            if (sMidY < SHOULDER_ZONE[2]) {
              say('Angle the camera up a bit — your shoulders should sit lower in the frame.', 'info');
            } else if (sMidY > SHOULDER_ZONE[3]) {
              say('Angle the camera down a bit — your shoulders should sit higher in the frame.', 'info');
            } else if (sMidX < SHOULDER_ZONE[0]) {
              say("Move a little to your right so you're centered.", 'info');
            } else {
              say("Move a little to your left so you're centered.", 'info');
            }
          } else if (!hipInZone) {
            if (hMidY < HIP_ZONE[2]) {
              say('Get closer or lower the camera — I need to see your hips too.', 'info');
            } else {
              say('Adjust the camera so your hips are in the lower half of the frame.', 'info');
            }
          }
        } else if (!stable) {
          if (cur !== 'holding') {
            setStateBoth('holding');
            say('Perfect spot — now hold still for a moment.', 'info');
          }
          holdStartRef.current = null;
          setHoldProgress(0);
        } else {
        // Wave 20.49p — Fresh-per-session framing capture. The old
        // gate required the rider to align to yesterday's saved
        // dots, which broke the day they switched cameras (laptop
        // vs external) and forced them to contort into an old
        // frame that no longer matched their setup. Now: the dots
        // move to whatever position the rider is holding, the
        // stability check locks it, and THAT position becomes the
        // fresh target — POSTed for cross-session context but
        // never used as a hard gate.
        if (cur !== 'holding') {
          setStateBoth('holding');
          say('Great, hold still…', 'info');
        }
        if (holdStartRef.current == null) holdStartRef.current = now;
        const held = now - holdStartRef.current;
        const prog = Math.min(1, held / HOLD_MS);
        setHoldProgress(prog);
        if (held >= HOLD_MS) {
          uprightPoseRef.current = { sx: sMidX, sy: sMidY, hx: hMidX, hy: hMidY };
          stabilityHistoryRef.current = [];
          holdStartRef.current = null;
          setHoldProgress(0);
          setStateBoth('locked_upright');
          say('Upright captured. Now get into your normal riding position.', 'success');
          // Capture-and-save the fresh target every session so
          // the depth-scale reference on the SessionBaseline
          // matches today's camera. Fire-and-forget.
          const v = videoRef.current;
          const imgW = v?.videoWidth ?? 640;
          const imgH = v?.videoHeight ?? 480;
          const shoulderHipPx = Math.hypot(
            (sMidX - hMidX) * imgW,
            (sMidY - hMidY) * imgH,
          );
          const capture: CalibrationTarget = {
            shoulderMid: { x: sMidX, y: sMidY },
            hipMid: { x: hMidX, y: hMidY },
            shoulderHipPx,
            capturedAt: new Date().toISOString(),
          };
          targetRef.current = capture;
          try {
            const token = window.localStorage.getItem(
              process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token',
            );
            if (token) {
              const api = process.env.REACT_APP_API || 'https://api.aero.chat';
              void fetch(`${api}/users/me/calibration-target`, {
                method: 'PUT',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(capture),
              }).catch(() => {});
            }
          } catch { /* storage disabled — target still in-memory for this session */ }

          // Wave 20.49q — Calibration snapshot for this session.
          // Encode the current MediaPipe segmentation mask + the
          // locked-in dot positions into a small JSON payload the
          // AI page can read at end-of-session. Falls back to a
          // dots-only payload when the mask is too big to fit
          // under the 30 KB cap or MediaPipe hasn't produced one
          // yet.
          try {
            const silhouettePngB64 =
              silhouetteToPngDataUrl(latestMaskRef.current) ?? null;
            // Wave 20.49s — capture A_cal (the rider's frontal
            // area in the guided upright pose, in real m²) so
            // the backend can back-solve a rig-neutral scale for
            // this session against the rider's historical median.
            //   1. Run auto-calibration on the same landmarks that
            //      just locked the pose → px/m + confidence.
            //   2. If confidence is high enough, count non-zero
            //      mask pixels → real m² area via px/m².
            //   3. Attach `aCalM2` to the snapshot payload; null
            //      when calibration wasn't confident enough to
            //      trust the scale.
            let aCalM2: number | null = null;
            try {
              const cal = calibrate(
                latestLandmarksRef.current,
                latestWorldLandmarksRef.current,
                imgW,
                imgH,
              );
              if (cal.is_usable && cal.px_per_m > 10) {
                const area = frontalAreaFromMask(
                  latestMaskRef.current,
                  cal.px_per_m,
                );
                if (area !== null) aCalM2 = area;
              }
            } catch { /* calibration unavailable — leave aCalM2 null */ }
            const snapshot = {
              shoulderMid: { x: sMidX, y: sMidY },
              hipMid: { x: hMidX, y: hMidY },
              silhouettePngB64,
              aCalM2,
              capturedAt: new Date().toISOString(),
            };
            window.localStorage.setItem(
              'aerochat.calibrationSnapshot',
              JSON.stringify(snapshot),
            );
          } catch { /* storage disabled — snapshot skipped */ }
        }
        }
      }

      timer = window.setTimeout(tick, 200);
    };

    (async () => {
      try {
        await detector.init();
        if (!cancelled) tick();
      } catch {
        // Detector unavailable — the Start button escape-hatch will
        // still open after ESCAPE_MS so the rider isn't fully blocked.
      }
    })();

    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
      try { detector.close(); } catch { /* already closed */ }
      try { handDetector.close(); } catch { /* already closed */ }
    };
  }, [cameraState, say, sensorsConfirmed]);

  // Wave 20.36 — after ESCAPE_MS of not locking, open the "start
  // anyway" override. Only starts counting once the camera is ready
  // (so the timer isn't burned by permission dialogs).
  useEffect(() => {
    if (cameraState !== 'ready') return;
    if (calState === 'locked') return;
    const id = window.setTimeout(() => setEscapeAllowed(true), ESCAPE_MS);
    return () => window.clearTimeout(id);
  }, [cameraState, calState]);

  // Wave 20.49t — Auto-start countdown replaced with the virtual
  // hand-gesture Start button (drawn on the video). The old
  // wall-clock countdown fired 3 s after `locked` regardless of
  // rider readiness, so reaching for the mouse to skip or cancel
  // routinely knocked the rider out of position. Now the rider
  // hovers their hand in the on-video circle to start; if they
  // aren't ready they simply don't reach out. `autoStartIn` stays
  // as a nullable value used by the (now-hidden) status pill in
  // case we ever want to re-surface a timing indicator, but it's
  // never written to.
  const autoStartIn: number | null = null;

  const cameraStatusState: 'on' | 'pending' | 'off' =
    cameraState === 'ready' ? 'on' : cameraState === 'requesting' ? 'pending' : 'off';
  const powerStatusState: 'on' | 'off' = ble.powerConnected ? 'on' : 'off';
  const hrStatusState: 'on' | 'off' = ble.hrConnected ? 'on' : 'off';
  // Cadence rides on the power-meter connection (combined sensor in
  // most modern power meters). Treat it as connected when power is.
  const cadenceStatusState: 'on' | 'off' =
    ble.powerConnected && ble.power?.cadenceRpm != null ? 'on' : 'off';

  // Wave 20.36 — Start button gates on calibration lock (or the
  // escape-hatch override after ESCAPE_MS of not locking).
  const canStart =
    cameraState === 'ready' && (calState === 'locked' || escapeAllowed);

  return (
    <Box sx={{ position: 'relative', minHeight: 'calc(100vh - 80px)', py: { xs: 3, md: 5 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2.5, md: 4 } }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="overline" sx={{ color: colors.brand }}>
            {t('preflight.overline')}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 800,
              letterSpacing: '-0.02em',
              mt: 1,
              mb: 1,
            }}
          >
            {t('preflight.title')}
          </Typography>
          <Typography sx={{ color: colors.textMuted, maxWidth: 640 }}>
            {t('preflight.subtitle')}
          </Typography>
          {focusClass && (
            <Box
              sx={{
                mt: 2,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                backgroundColor: 'rgba(106, 255, 69, 0.08)',
                border: `1px solid ${colors.borderFocus}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 800,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: colors.brand,
                }}
              >
                {focusClass === 'new' ? 'Recording' : 'Refining'}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  color: colors.text,
                }}
              >
                {focusLabel(focusClass)}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.5fr) minmax(280px, 1fr)' },
            gap: 3,
          }}
        >
          {/* Mirror */}
          <Box
            sx={{
              position: 'relative',
              borderRadius: 4,
              overflow: 'hidden',
              backgroundColor: '#000',
              border: `1px solid ${
                cameraState === 'ready' ? colors.borderFocus : colors.border
              }`,
              boxShadow:
                cameraState === 'ready'
                  ? `0 0 0 1px ${colors.brandShadow}, 0 24px 56px rgba(106, 255, 69, 0.18)`
                  : 'none',
              transition: 'box-shadow 220ms, border-color 220ms',
              // Wave 20.38 — Rider is vertical (head → hip). 16:9
              // landscape crops the top/bottom of the frame, making
              // it hard to get the shoulder + hip midpoints into the
              // calibration zones. 3:4 gives real vertical room and
              // keeps horizontal wide enough for arm reach + bike.
              aspectRatio: '3 / 4',
              // Cap absolute height so the video doesn't dominate a
              // tall monitor; on mobile the grid switches to single-
              // column and the video naturally goes wider.
              maxHeight: { xs: 'none', md: 'calc(100vh - 220px)' },
              mx: 'auto',
              width: '100%',
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                // Mirror horizontally so the rider sees themselves as
                // they would in a mirror, not the camera's truth.
                transform: 'scaleX(-1)',
                display: cameraState === 'ready' ? 'block' : 'none',
              }}
            />
            {cameraState !== 'ready' && (
              <Stack
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ position: 'absolute', inset: 0, color: colors.textMuted, textAlign: 'center', px: 3 }}
              >
                {cameraState === 'requesting' && <CircularProgress size={28} sx={{ color: colors.brand }} />}
                <Typography sx={{ fontSize: '0.875rem', maxWidth: 360 }}>
                  {cameraState === 'requesting' && t('preflight.cameraRequesting')}
                  {cameraState === 'denied' && (cameraError ?? t('preflight.cameraDenied'))}
                  {cameraState === 'unavailable' && (cameraError ?? t('preflight.cameraUnavailable'))}
                  {cameraState === 'idle' && t('preflight.cameraIdle')}
                </Typography>
              </Stack>
            )}
            {/* Wave 20.49cu — the Live pill moved into the
                GestureActionBar's right slot (rendered below) so the
                top strip has one owner and nothing overlaps. */}

            {/* Wave 20.36 — Calibration lock overlays. Two target
                zones for shoulders + hips, a center line, a moving
                dot at the current shoulder midpoint, and a big
                status card at the top showing progress toward lock. */}
            {cameraState === 'ready' && (
              <CalibrationOverlay
                calState={calState}
                holdProgress={holdProgress}
                posePoints={posePoints}
                landmarksRef={latestLandmarksRef}
                videoRef={videoRef}
                calibrationHealth={calibrationHealth}
                topOffset={GESTURE_BAR_HEIGHT}
              />
            )}

            {/* Wave 20.49cu — GestureActionBar: a full-width strip
                across the top of the video that OWNS that space. Big
                pill buttons; the Live pill lives in its right slot.
                Takes the hand-detector REF (read per frame) and the
                video element (object-fit: cover crop math — Preflight
                is a 3:4 box over a 16:9 camera, so without it the
                hit-test was off by up to a third of the width).
                Actions follow the calibration state machine; the bar
                itself stays mounted so the Live pill persists. */}
            {cameraState === 'ready' && (
              <GestureActionBar
                mirrorX
                handsRef={latestHandsRef}
                videoRef={videoRef}
                actions={
                  calState === 'awaiting_start'
                    ? [
                        {
                          id: 'start-calibration',
                          label: 'Start Calibration',
                          icon: <AdjustIcon />,
                          tone: 'primary',
                          onFire: () => {
                            calStateRef.current = 'detecting';
                            setCalState('detecting');
                          },
                        },
                      ]
                    : calState === 'locked'
                    ? [
                        {
                          id: 'start-session',
                          label: 'Start Session',
                          icon: <PlayArrowIcon />,
                          tone: 'primary',
                          onFire: () => liability.guard(onStart),
                        },
                      ]
                    : []
                }
                onArm={(id) => {
                  if (id === 'start-calibration') {
                    say('Ready to start calibration — hold, or thumbs up.', 'info');
                  } else if (id === 'start-session') {
                    say('Ready to start session — hold, or thumbs up.', 'info');
                  }
                }}
                trailing={
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.375,
                      borderRadius: 999,
                      backgroundColor: 'rgba(0, 0, 0, 0.55)',
                      backdropFilter: 'blur(12px)',
                      border: `1px solid ${colors.borderFocus}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.625,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: colors.brand,
                        boxShadow: `0 0 6px ${colors.brand}`,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        color: colors.text,
                        textTransform: 'uppercase',
                      }}
                    >
                      {t('preflight.live')}
                    </Typography>
                  </Box>
                }
              />
            )}
          </Box>

          {/* Status panel */}
          <Stack spacing={1.5}>
            {/* Wave 20.36 — Calibration lock card. Bigger + more
                central than the other status pills because it's the
                gate; Start won't unlock until this reads LOCKED. */}
            {cameraState === 'ready' && (
              <CalibrationCard
                calState={calState}
                holdProgress={holdProgress}
                voiceEnabled={voiceEnabled}
                onToggleVoice={() => setVoiceEnabled((v) => !v)}
                chatLog={chatLog}
              />
            )}
            <StatusPill
              icon={<VideocamOutlinedIcon sx={{ fontSize: 18 }} />}
              label={t('preflight.status.camera')}
              state={cameraStatusState}
            />
            <StatusPill
              icon={<BoltIcon sx={{ fontSize: 18 }} />}
              label={t('preflight.status.power')}
              state={powerStatusState}
            />
            <StatusPill
              icon={<FavoriteBorderIcon sx={{ fontSize: 18 }} />}
              label={t('preflight.status.heartRate')}
              state={hrStatusState}
            />
            <StatusPill
              icon={<LoopIcon sx={{ fontSize: 18 }} />}
              label={t('preflight.status.cadence')}
              state={cadenceStatusState}
            />

            {/* Bluetooth connect / disconnect controls. Reuses the
                existing in-session BluetoothPanel — same UX as during
                the ride. */}
            <BluetoothPanel ble={ble} />

            {/* Wave 20.44 — ERG target power. Only surfaces when the
                paired power meter is a controllable smart trainer
                (FTMS). Sets the trainer to hold this wattage so the
                aero session runs at a steady effort — steady power
                is the whole point of an aero measurement. */}
            {ble.hasErgControl && (
              <Box
                sx={{
                  p: 1.75,
                  borderRadius: 2,
                  border: `1px solid ${colors.borderFocus}`,
                  backgroundColor: 'rgba(106,255,69,0.06)',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <BoltIcon sx={{ color: colors.brand, fontSize: 18 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: colors.brand,
                      }}
                    >
                      ERG target
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted, mt: 0.25 }}>
                      Trainer holds you at this wattage during the
                      session.
                    </Typography>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Button
                      size="small"
                      onClick={() => ble.setTargetPower((ble.targetPowerW ?? 200) - 10)}
                      sx={{
                        minWidth: 32,
                        color: colors.text,
                        borderColor: colors.border,
                        border: `1px solid ${colors.border}`,
                      }}
                    >
                      −
                    </Button>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1.5,
                        border: `1px solid ${colors.brand}66`,
                        backgroundColor: 'rgba(10,14,26,0.6)',
                        minWidth: 72,
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontWeight: 800,
                          color: colors.brand,
                          fontSize: '1rem',
                          lineHeight: 1,
                        }}
                      >
                        {ble.targetPowerW ?? 200}
                        <Box component="span" sx={{ fontSize: '0.7rem', ml: 0.5, color: colors.textMuted }}>
                          W
                        </Box>
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => ble.setTargetPower((ble.targetPowerW ?? 200) + 10)}
                      sx={{
                        minWidth: 32,
                        color: colors.text,
                        border: `1px solid ${colors.border}`,
                      }}
                    >
                      +
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            )}

            {liability.needsAccept && (
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  color: colors.warning,
                  mt: -0.5,
                  mb: 0.5,
                  fontWeight: 600,
                }}
              >
                You'll be asked to accept the Liability Release before starting.
              </Typography>
            )}
            <Button
              variant="contained"
              size="large"
              disabled={!canStart || !liability.loaded}
              onClick={() => liability.guard(onStart)}
              startIcon={<PlayArrowIcon />}
              sx={{
                mt: 1,
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                backgroundColor: colors.brand,
                color: colors.bg,
                boxShadow: canStart ? `0 12px 32px ${colors.brandShadow}` : 'none',
                '&:hover': { backgroundColor: colors.brandBright },
                '&:disabled': { backgroundColor: colors.surfaceElevated, color: colors.textFaint },
              }}
            >
              {autoStartIn != null
                ? `Starting in ${autoStartIn}…`
                : t('preflight.start')}
            </Button>
            {/* Wave 20.37 — nudge for the auto-start countdown so users
                know why the button label changed. */}
            {autoStartIn != null && (
              <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted }}>
                Hold your position — session begins automatically. Move to cancel.
              </Typography>
            )}
            {/* Wave 20.36 — explain WHY the button is locked when it is,
                and surface the escape hatch once ESCAPE_MS elapses. */}
            {cameraState === 'ready' && calState !== 'locked' && !escapeAllowed && (
              <Typography sx={{ fontSize: '0.75rem', color: colors.textFaint }}>
                Complete calibration first (the video overlay will guide you).
              </Typography>
            )}
            {cameraState === 'ready' && calState !== 'locked' && escapeAllowed && (
              <Typography sx={{ fontSize: '0.75rem', color: colors.warning }}>
                Trouble locking? You can start anyway — the session pipeline
                will keep trying to calibrate as you ride.
              </Typography>
            )}
            {cameraState !== 'ready' && (
              <Typography sx={{ fontSize: '0.75rem', color: colors.textFaint }}>
                {t('preflight.cameraNote')}
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>
      {liability.Modal}

      {/* Wave 20.44 — Sensor pairing gate. Blocks calibration +
          auto-start until the rider explicitly picks: connect a
          sensor, or Continue without. Only shows after the camera
          is ready (nothing else to do before then). */}
      <Dialog
        open={cameraState === 'ready' && !sensorsConfirmed}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              backgroundColor: colors.surfaceElevated,
              border: `1px solid ${colors.borderFocus}`,
            },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Pair your sensors</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.textMuted, fontSize: '0.9rem', mb: 2 }}>
            Optional but recommended. A power meter calibrates your
            CdA over time and lets you see how many watts each
            position saves. HR is just for context. You can skip
            both and use the camera-only measurement.
          </Typography>
          <Stack spacing={1.25}>
            <Button
              variant={ble.powerConnected ? 'contained' : 'outlined'}
              startIcon={<BoltIcon />}
              onClick={() => (ble.powerConnected ? ble.disconnectPower() : ble.connectPower())}
              sx={{
                borderColor: ble.powerConnected ? colors.brand : colors.border,
                backgroundColor: ble.powerConnected ? colors.brand : 'transparent',
                color: ble.powerConnected ? colors.bg : colors.text,
                justifyContent: 'flex-start',
                '&:hover': {
                  borderColor: colors.brand,
                  backgroundColor: ble.powerConnected ? colors.brandBright : 'rgba(106,255,69,0.05)',
                },
              }}
            >
              {ble.powerConnected
                ? `Power connected · ${ble.powerDevice ?? 'meter'}`
                : 'Connect power meter'}
            </Button>
            <Button
              variant={ble.hrConnected ? 'contained' : 'outlined'}
              startIcon={<FavoriteBorderIcon />}
              onClick={() => (ble.hrConnected ? ble.disconnectHr() : ble.connectHr())}
              sx={{
                borderColor: ble.hrConnected ? colors.brand : colors.border,
                backgroundColor: ble.hrConnected ? colors.brand : 'transparent',
                color: ble.hrConnected ? colors.bg : colors.text,
                justifyContent: 'flex-start',
                '&:hover': {
                  borderColor: colors.brand,
                  backgroundColor: ble.hrConnected ? colors.brandBright : 'rgba(106,255,69,0.05)',
                },
              }}
            >
              {ble.hrConnected
                ? `HR connected · ${ble.hrDevice ?? 'monitor'}`
                : 'Connect heart rate monitor'}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setSensorsConfirmed(true)}
            sx={{
              backgroundColor: colors.brand,
              color: colors.bg,
              fontWeight: 700,
              '&:hover': { backgroundColor: colors.brandBright },
            }}
          >
            {ble.powerConnected || ble.hrConnected
              ? 'Continue'
              : 'Continue without sensors'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
